/* AsyncDocxButton — smart Word-export button.
 *
 * Behavior:
 *  1. POST-equivalent GET to ``${apiRoot}/projects/{pid}/deliverables/{did}/export.docx``
 *  2. If the response is a binary DOCX (Content-Type starts with the OOXML
 *     prefix) → trigger a blob download immediately (cache hit).
 *  3. If the response is JSON with status 202 → open a tiny inline progress
 *     indicator and poll ``status_url`` every 3s. When status === 'succeeded'
 *     fetch ``download_url`` and trigger the blob download.
 *  4. Errors surface via toast + button label; ``failed`` jobs show the
 *     server-side error string.
 *
 * Drop-in replacement for the older `<a href="...export.docx">` link. The
 * trigger is a <button> styled identically.
 */
import React, { useState } from "react";
import { FileType2, Loader2 } from "lucide-react";

const OOXML_DOCX = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 200);
}

function fileFromHeaders(resp, fallback) {
  const cd = resp.headers.get("content-disposition") || "";
  const m = /filename="([^"]+)"/.exec(cd);
  return m ? m[1] : fallback;
}

async function fetchAuth(url) {
  // include cookie session
  return fetch(url, { credentials: "include" });
}

export default function AsyncDocxButton({
  apiRoot,
  projectId,
  deliverableId,
  deliverableCode = "deliv",
  version = 1,
  style,
  testid,
  className = "btn btn-ghost",
  pollIntervalMs = 3000,
  maxWaitMs = 600000,    // 10 min cap
}) {
  const [status, setStatus] = useState("idle");
  // idle | starting | running | downloading | done | failed
  const [error, setError] = useState("");

  const startUrl = `${apiRoot}/projects/${projectId}/deliverables/${deliverableId}/export.docx`;
  const fallbackName = `${(deliverableCode || "deliv").replace(/\//g, "_")}_v${version}.docx`;

  const onClick = async () => {
    setError("");
    setStatus("starting");
    try {
      const resp = await fetchAuth(startUrl);
      const ct = resp.headers.get("content-type") || "";

      // Direct cache-hit binary
      if (ct.startsWith(OOXML_DOCX) || ct.startsWith("application/octet-stream")) {
        const blob = await resp.blob();
        downloadBlob(blob, fileFromHeaders(resp, fallbackName));
        setStatus("done");
        setTimeout(() => setStatus("idle"), 1500);
        return;
      }

      // 202 + JSON job payload
      if (resp.status === 202 && ct.startsWith("application/json")) {
        const payload = await resp.json();
        const statusUrl = `${apiRoot}${payload.status_url}`.replace(/^\/api\/api/, "/api");
        const downloadUrl = `${apiRoot}${payload.download_url}`.replace(/^\/api\/api/, "/api");
        setStatus("running");

        const startedAt = Date.now();
        // Poll loop
        // eslint-disable-next-line no-constant-condition
        while (true) {
          if (Date.now() - startedAt > maxWaitMs) {
            throw new Error(`Generation exceeded ${maxWaitMs / 1000}s`);
          }
          await new Promise((r) => setTimeout(r, pollIntervalMs));
          const sresp = await fetchAuth(statusUrl);
          const sjson = await sresp.json();
          if (sjson.status === "failed") {
            throw new Error(sjson.error || "Job failed");
          }
          if (sjson.status === "succeeded") break;
        }

        setStatus("downloading");
        const dresp = await fetchAuth(downloadUrl);
        if (!dresp.ok) throw new Error(`Download failed (HTTP ${dresp.status})`);
        const blob = await dresp.blob();
        downloadBlob(blob, fileFromHeaders(dresp, fallbackName));
        setStatus("done");
        setTimeout(() => setStatus("idle"), 1500);
        return;
      }

      // Unexpected
      const text = await resp.text();
      throw new Error(`Unexpected response (HTTP ${resp.status}) ${text.slice(0, 120)}`);
    } catch (e) {
      setError(e.message || String(e));
      setStatus("failed");
      setTimeout(() => setStatus("idle"), 4000);
    }
  };

  const labelMap = {
    idle: "Word",
    starting: "Starting…",
    running: "Generating…",
    downloading: "Downloading…",
    done: "Saved ✓",
    failed: "Retry",
  };
  const isWorking = ["starting", "running", "downloading"].includes(status);

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isWorking}
      className={className}
      style={{ ...style, cursor: isWorking ? "wait" : "pointer", opacity: isWorking ? 0.85 : 1 }}
      data-testid={testid || `deliv-docx-${deliverableCode}`}
      title={error || "Download as Microsoft Word (.docx). Large deliverables generate in the background."}
      aria-busy={isWorking}
    >
      {isWorking ? <Loader2 size={14} className="spin" /> : <FileType2 size={14} />}
      &nbsp;{labelMap[status] || "Word"}
    </button>
  );
}
